import { useTheme } from '@backoffice-frontend/patterns'
import type { ReactNode } from 'react'
import type { BackofficeAreas } from '../types/BackofficeArea'
import type { SectionsConfig } from '../types/NavigationSectionsConfig'
import { NavigationDrawerSection } from './NavigationDrawerSection'

type NavigationDrawerContentProps = {
  navigationSections: SectionsConfig
  filters?: ReactNode
  onAreaChange?: VoidFunction
  open: boolean
}

export const NavigationDrawerContent = ({
  navigationSections,
  filters,
  onAreaChange,
  open,
}: NavigationDrawerContentProps) => {
  const theme = useTheme()

  return (
    <div
      className="navigation-content"
      css={{
        [theme.breakpoints.up('sm')]: {
          margin: `0 ${theme.space.Space4}`,
          paddingTop: theme.space.Space4,
          paddingBottom: theme.space.Space4,
          height: 'calc(100vh - 136px)',
        },
        [theme.breakpoints.down('md')]: {
          margin: 0,
          height: 'calc(100vh - 48px)',
        },
        overflowY: 'auto',
        overflowX: 'visible',
        position: 'relative',
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': {
          display: 'none',
        },
        // This creates a "Fade Out" effect at the top (24px) and bottom (24px)
        maskImage:
          'linear-gradient(to bottom, transparent 0%, black 24px, black calc(100% - 24px), transparent 100%)',
      }}
    >
      {filters}
      <div
        css={{
          [theme.breakpoints.down('md')]: {
            padding: theme.space.Space2,
          },
          display: 'grid',
          gap: theme.space.Space8,
        }}
      >
        {navigationSections.map((section, index) => {
          if (section === null || section.hiddenInNavBar) return null
          const sectionAreas = section.areas.filter(
            (area): area is NonNullable<BackofficeAreas[number]> =>
              area !== null,
          )

          if (sectionAreas.length === 0) return null

          return (
            <NavigationDrawerSection
              // biome-ignore lint/suspicious/noArrayIndexKey: not all sections have labels which can be used as key
              key={index}
              onAreaChange={onAreaChange}
              open={open}
              label={section.label ? section.label : undefined}
              areas={sectionAreas}
            />
          )
        })}
      </div>
    </div>
  )
}
